/**
 * Manage Channel View
 * 
 * Page for managing a connected channel's accounts and imported units.
 * Now uses module's own hooks and services.
 */

import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ToastMessageContext } from '@context';
import { 
  useChannelByTypeQuery, 
  useChannelUnitsForImportQuery, 
  useUpdateListingStatusMutation,
  useDisconnectAccountMutation,
  useResyncAccountMutation,
} from '../../hooks';
import { ChannelName, IApiConnectedAccount, SyncStatus } from '../../types';
import { T_KEYS, CHANNEL_INFO } from '../../constants';
import { ErrorState, BookingAccountsStep, ImportedUnitsTable } from '../../components';
import { PageContainer } from '../../styles';
// Local components - now in module
import { ChannelManageHeader, AccountTabs, AccountInfoBar, BookingAccountSection, AddBookingPropertyModal } from '../../components/ManageChannel';
import { RefreshSyncConfirmModal } from '../../components/Modals';
import {
  ManagePageContent,
  ManagePageCard,
  HeaderSkeleton,
  HeaderSkeletonLeft,
  SkeletonLogo,
  SkeletonTitle,
  SkeletonDescription,
  SkeletonButton,
  SkeletonTabs,
  SkeletonTab,
  SkeletonInfoBar,
  SkeletonInfoBarLeft,
  SkeletonBadge,
  SkeletonInfoBarRight,
  SkeletonActionButton,
  DisconnectedAlertContainer,
  DisconnectedAlertText,
  DisconnectedAlertIcon,
} from '../../styles/ManageChannelStyles';

const AlertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 6.66667V10M10 13.3333H10.0083M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ManageChannelView: FC = () => {
  const navigate = useNavigate();
  const { integration } = useParams<{ integration: string }>();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';
  const { showSuccess, showError } = useContext(ToastMessageContext);

  // Validate channel name
  const channelName = integration as ChannelName;
  const validChannels: ChannelName[] = ['airbnb', 'booking', 'gathern'];
  
  // Redirect if invalid channel
  if (!validChannels.includes(channelName)) {
    navigate('/channels');
    return null;
  }

  // URL search params for account persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const accountIdFromUrl = searchParams.get('accountId');
  const subAccountIdFromUrl = searchParams.get('subAccountId');

  // State
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(0);
  const [selectedSubAccountIndex, setSelectedSubAccountIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'synced' | 'not_synced' | 'failed'>('all');
  const [showBookingAccountDetails, setShowBookingAccountDetails] = useState(!!accountIdFromUrl);
  const [isInitializedFromUrl, setIsInitializedFromUrl] = useState(false);
  const [accountNotFound, setAccountNotFound] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showAllUnitsSyncedModal, setShowAllUnitsSyncedModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDisconnectConfirmModal, setShowDisconnectConfirmModal] = useState(false);

  // Fetch channel data using module hook
  const { 
    data: channelData, 
    isLoading: isLoadingChannel, 
    isError: isChannelError, 
    error: channelError,
    refetch: refetchChannel,
  } = useChannelByTypeQuery(channelName);

  // Get connected accounts directly from API response
  const connectedAccounts = useMemo((): IApiConnectedAccount[] => {
    if (!channelData?.connectedAccounts) return [];
    return channelData.connectedAccounts;
  }, [channelData]);

  // Initialize selected account from URL
  useEffect(() => {
    if (connectedAccounts.length > 0 && !isInitializedFromUrl) {
      if (accountIdFromUrl) {
        const accountIndex = connectedAccounts.findIndex(
          account => String(account.id) === accountIdFromUrl
        );
        
        if (accountIndex >= 0) {
          setSelectedAccountIndex(accountIndex);
          
          if (channelName === 'booking') {
            setShowBookingAccountDetails(true);
            
            if (subAccountIdFromUrl) {
              const account = connectedAccounts[accountIndex];
              const subAccounts = account.subAccounts || [];
              const subAccountIndex = subAccounts.findIndex(
                sub => String(sub.id) === subAccountIdFromUrl
              );
              if (subAccountIndex >= 0) {
                setSelectedSubAccountIndex(subAccountIndex);
              }
            }
          }
        } else {
          setAccountNotFound(true);
        }
      }
      setIsInitializedFromUrl(true);
    }
  }, [connectedAccounts, accountIdFromUrl, subAccountIdFromUrl, isInitializedFromUrl, channelName]);

  const selectedAccount = connectedAccounts[selectedAccountIndex] || null;

  const selectedSubAccount = useMemo(() => {
    if (channelName !== 'booking' || !selectedAccount) return null;
    const subAccounts = selectedAccount.subAccounts || [];
    return subAccounts[selectedSubAccountIndex] || null;
  }, [channelName, selectedAccount, selectedSubAccountIndex]);

  // Update URL when selected account changes
  useEffect(() => {
    if (isInitializedFromUrl && selectedAccount) {
      if (channelName === 'booking' && !showBookingAccountDetails) {
        if (accountIdFromUrl || subAccountIdFromUrl) {
          setSearchParams({}, { replace: true });
        }
        return;
      }
      
      const currentAccountId = String(selectedAccount.id);
      const params: Record<string, string> = { accountId: currentAccountId };
      
      if (channelName === 'booking' && selectedSubAccount) {
        params.subAccountId = String(selectedSubAccount.id);
      }
      
      const urlAccountId = accountIdFromUrl;
      const urlSubAccountId = subAccountIdFromUrl;
      if (currentAccountId !== urlAccountId || 
          (selectedSubAccount && String(selectedSubAccount.id) !== urlSubAccountId)) {
        setSearchParams(params, { replace: true });
      }
    }
  }, [isInitializedFromUrl, selectedAccount, selectedSubAccount, channelName, showBookingAccountDetails, accountIdFromUrl, subAccountIdFromUrl, setSearchParams, selectedSubAccountIndex]);

  // Get channelId for API call
  const channelId = useMemo(() => {
    if (channelName === 'booking' && !showBookingAccountDetails) {
      return null;
    }
    
    if (!selectedAccount) return null;
    
    if (channelName === 'booking') {
      if (selectedAccount.connectionType === 2 && selectedSubAccount) {
        return selectedSubAccount.channelId || null;
      }
      return selectedAccount.channelId || null;
    }
    
    return selectedAccount.channelId || null;
  }, [channelName, selectedAccount, selectedSubAccount, showBookingAccountDetails]);

  // Fetch units using module hook
  const {
    data: unitsData,
    isLoading: isLoadingUnits,
    isFetching: isFetchingUnits,
    isError: isUnitsError,
    error: unitsError,
    refetch: refetchUnits,
  } = useChannelUnitsForImportQuery(channelName, channelId, selectedAccount?.id?.toString());

  // Update listing status mutation using module hook
  const updateListingStatusMutation = useUpdateListingStatusMutation();

  // Disconnect account mutation
  const disconnectAccountMutation = useDisconnectAccountMutation();

  // Resync account mutation
  const resyncAccountMutation = useResyncAccountMutation();

  // Filter units
  const filteredUnits = useMemo(() => {
    if (!unitsData?.units) return [];
    
    let filtered = unitsData.units.filter(unit => unit.syncStatus !== SyncStatus.NotSynced);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(unit => 
        unit.name.toLowerCase().includes(query) ||
        unit.address.toLowerCase().includes(query) ||
        unit.propertyId.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(unit => {
        if (statusFilter === 'synced') return unit.isSynced;
        if (statusFilter === 'not_synced') return !unit.isSynced;
        return true;
      });
    }

    return filtered;
  }, [unitsData, searchQuery, statusFilter]);

  // Handlers
  const handleAddAccount = useCallback(() => {
    navigate(`/channels/${channelName}/connect`);
  }, [navigate, channelName]);

  const handleAccountChange = useCallback((index: number) => {
    setSelectedAccountIndex(index);
    setSelectedSubAccountIndex(0);
  }, []);

  const handleSubAccountChange = useCallback((index: number) => {
    setSelectedSubAccountIndex(index);
  }, []);

  const handleImportNewUnits = useCallback(() => {
    // Check if there are any unsynced units available to import
    const hasUnsyncedUnits = unitsData?.units?.some(unit => !unit.isSynced);
    
    if (hasUnsyncedUnits) {
      // There are units to import, navigate to import page
      navigate(`/channels/${channelName}/units/import?channelId=${channelId}`);
    } else {
      // All units are already synced, show modal
      setShowAllUnitsSyncedModal(true);
    }
  }, [navigate, channelName, channelId, unitsData]);

  // Refresh Sync Modal state
  const [isRefreshSyncModalOpen, setIsRefreshSyncModalOpen] = useState(false);
  const [isRefreshSyncLoading, setIsRefreshSyncLoading] = useState(false);

  const handleOpenRefreshSyncModal = useCallback(() => {
    setIsRefreshSyncModalOpen(true);
  }, []);

  const handleCloseRefreshSyncModal = useCallback(() => {
    if (!isRefreshSyncLoading) {
      setIsRefreshSyncModalOpen(false);
    }
  }, [isRefreshSyncLoading]);

  const handleConfirmRefreshSync = useCallback(async () => {
    setIsRefreshSyncLoading(true);
    try {
      // Wait for the API call to complete
      const result = await refetchUnits();
      // Log to verify the API completed
      console.log('Refresh sync completed:', result.status);
    } catch (error) {
      console.error('Refresh sync error:', error);
    } finally {
      setIsRefreshSyncLoading(false);
      setIsRefreshSyncModalOpen(false);
    }
  }, [refetchUnits]);

  const handleRefreshSync = useCallback(async () => {
    await refetchUnits();
  }, [refetchUnits]);

  const handleBackToChannels = useCallback(() => {
    navigate('/channels');
  }, [navigate]);

  const handleUnitAction = useCallback(async (unitId: string, actionType: number) => {
    if (!unitsData?.userChannelId || !unitsData?.channelType) {
      showError(t('CHANNELS.ERROR_MISSING_CHANNEL_DATA'));
      return;
    }

    try {
      await updateListingStatusMutation.mutateAsync({
        listingId: unitId,
        userChannelId: unitsData.userChannelId,
        channelType: unitsData.channelType,
        action: actionType,
      });

      showSuccess(t(T_KEYS.ACTION_SUCCESS) || 'Action completed successfully');
      refetchUnits();
    } catch (error: any) {
      const errorMessage = error?.messageAr || error?.message || t(T_KEYS.ACTION_FAILED) || 'Action failed';
      showError(errorMessage);
    }
  }, [unitsData, updateListingStatusMutation, showSuccess, showError, t, refetchUnits]);

  const handleBookingAccountSelect = useCallback((account: IApiConnectedAccount) => {
    const accountIndex = connectedAccounts.findIndex(a => a.id === account.id);
    if (accountIndex >= 0) {
      setSelectedAccountIndex(accountIndex);
      setShowBookingAccountDetails(true);
    }
  }, [connectedAccounts]);

  const handleBackToBookingAccounts = useCallback(() => {
    setShowBookingAccountDetails(false);
  }, []);

  const handleAddProperty = useCallback(() => {
    setShowAddPropertyModal(true);
  }, []);

  const handleAddPropertyModalClose = useCallback(() => {
    setShowAddPropertyModal(false);
  }, []);

  const handleAddPropertySuccess = useCallback(() => {
    refetchChannel();
    refetchUnits();
  }, [refetchChannel, refetchUnits]);

  // Settings modal handlers
  const handleOpenSettings = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setShowSettingsModal(false);
  }, []);

  const handleDisconnectClick = useCallback(() => {
    setShowDisconnectConfirmModal(true);
  }, []);

  const handleDisconnectCancel = useCallback(() => {
    if (!disconnectAccountMutation.isPending) {
      setShowDisconnectConfirmModal(false);
    }
  }, [disconnectAccountMutation.isPending]);

  const handleDisconnectConfirm = useCallback(async () => {
    if (!selectedAccount?.id) return;

    try {
      await disconnectAccountMutation.mutateAsync({
        userChannelId: selectedAccount.id,
      });
      
      // Show success toast
      showSuccess(t(T_KEYS.DISCONNECT_SUCCESS));
      
      // Close modals
      setShowDisconnectConfirmModal(false);
      setShowSettingsModal(false);
      
      // Refetch channel data to display updated state
      refetchChannel();
    } catch (error) {
      // Show error toast
      const errorMessage = error instanceof Error ? error.message : t(T_KEYS.DISCONNECT_ERROR);
      showError(errorMessage);
    }
  }, [selectedAccount?.id, disconnectAccountMutation, showSuccess, showError, t, refetchChannel]);

  // Handle resync/reconnect account
  const handleResyncAccount = useCallback(async () => {
    if (!selectedAccount?.id) return;

    try {
      await resyncAccountMutation.mutateAsync({
        userChannelId: selectedAccount.id,
      });
      
      // Show success toast
      showSuccess(t(T_KEYS.RESYNC_SUCCESS) || 'Account reconnected successfully');
      
      // Refetch channel data to display updated state
      refetchChannel();
      refetchUnits();
    } catch (error) {
      // Show error toast
      const errorMessage = error instanceof Error ? error.message : (t(T_KEYS.RESYNC_ERROR) || 'Failed to reconnect account');
      showError(errorMessage);
    }
  }, [selectedAccount?.id, resyncAccountMutation, showSuccess, showError, t, refetchChannel, refetchUnits]);

  const isLoading = isLoadingChannel || isLoadingUnits;

  // Error states
  if (isChannelError) {
    return (
      <PageContainer>
        <ErrorState
          message={channelError?.message}
          onRetry={() => refetchChannel()}
        />
      </PageContainer>
    );
  }

  if (accountNotFound && !isLoadingChannel) {
    const handleGoToAccounts = () => {
      setAccountNotFound(false);
      setSearchParams({}, { replace: true });
      if (channelName === 'booking') {
        setShowBookingAccountDetails(false);
      }
    };

    return (
      <PageContainer>
        <ErrorState
          message={t(T_KEYS.ACCOUNT_NOT_FOUND, { id: accountIdFromUrl })}
          onRetry={handleGoToAccounts}
          retryLabel={t(T_KEYS.VIEW_ALL_ACCOUNTS)}
        />
      </PageContainer>
    );
  }

  if (!isLoadingChannel && channelData && channelData.numberOfConnectedAccounts === 0) {
    navigate(`/channels/${channelName}/connect`);
    return null;
  }

  const channelInfo = CHANNEL_INFO[channelName];

  // Loading skeleton
  if (isLoadingChannel) {
    if (channelName === 'booking') {
      if (accountIdFromUrl) {
        return (
          <PageContainer>
            <HeaderSkeleton>
              <HeaderSkeletonLeft>
                <SkeletonLogo />
                <div>
                  <SkeletonTitle />
                  <SkeletonDescription />
                </div>
              </HeaderSkeletonLeft>
              <SkeletonButton />
            </HeaderSkeleton>
            <ManagePageContent>
              <ManagePageCard>
                <SkeletonInfoBar>
                  <SkeletonInfoBarLeft>
                    <SkeletonTitle style={{ width: '150px', marginBottom: 0 }} />
                    <SkeletonBadge />
                  </SkeletonInfoBarLeft>
                  <SkeletonInfoBarRight>
                    <SkeletonActionButton />
                    <SkeletonActionButton />
                  </SkeletonInfoBarRight>
                </SkeletonInfoBar>
              </ManagePageCard>
            </ManagePageContent>
          </PageContainer>
        );
      }

      return (
        <PageContainer>
          <ManagePageContent>
            <HeaderSkeleton>
              <HeaderSkeletonLeft>
                <SkeletonLogo style={{ width: 40, height: 40 }} />
                <div>
                  <SkeletonTitle style={{ width: '150px' }} />
                  <SkeletonDescription style={{ width: '250px' }} />
                </div>
              </HeaderSkeletonLeft>
              <SkeletonButton style={{ width: '200px' }} />
            </HeaderSkeleton>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <ManagePageCard style={{ flex: 1, minWidth: '300px', maxWidth: 'calc(50% - 8px)' }}>
                <SkeletonInfoBar style={{ flexDirection: 'column', alignItems: 'stretch', gap: '16px' }}>
                  <SkeletonInfoBarLeft style={{ justifyContent: 'space-between' }}>
                    <SkeletonTitle style={{ width: '120px', marginBottom: 0 }} />
                    <SkeletonBadge />
                  </SkeletonInfoBarLeft>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '40px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <SkeletonTitle style={{ width: '30px', height: '24px', marginBottom: '4px' }} />
                      <SkeletonDescription style={{ width: '40px' }} />
                    </div>
                  </div>
                  <SkeletonButton style={{ width: '100%', height: '44px' }} />
                </SkeletonInfoBar>
              </ManagePageCard>
            </div>
          </ManagePageContent>
        </PageContainer>
      );
    }

    return (
      <PageContainer>
        <HeaderSkeleton>
          <HeaderSkeletonLeft>
            <SkeletonLogo />
            <div>
              <SkeletonTitle />
              <SkeletonDescription />
            </div>
          </HeaderSkeletonLeft>
          <SkeletonButton />
        </HeaderSkeleton>
        <ManagePageContent>
          <SkeletonTabs>
            <SkeletonTab />
            <SkeletonTab />
            <SkeletonTab />
          </SkeletonTabs>
          <ManagePageCard>
            <SkeletonInfoBar>
              <SkeletonInfoBarLeft>
                <SkeletonTitle style={{ width: '150px', marginBottom: 0 }} />
                <SkeletonBadge />
              </SkeletonInfoBarLeft>
              <SkeletonInfoBarRight>
                <SkeletonActionButton />
                <SkeletonActionButton />
              </SkeletonInfoBarRight>
            </SkeletonInfoBar>
          </ManagePageCard>
        </ManagePageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {channelName !== 'booking' && (
        <ChannelManageHeader
          channelName={channelName}
          channelDisplayName={isRtl ? channelInfo.displayNameAr : channelInfo.displayName}
          onAddAccount={handleAddAccount}
          onBack={handleBackToChannels}
        />
      )}

      <ManagePageContent>
        {connectedAccounts.length > 0 && channelName !== 'booking' && (
          <AccountTabs
            accounts={connectedAccounts}
            selectedIndex={selectedAccountIndex}
            onSelectAccount={handleAccountChange}
            channelName={channelName}
          />
        )}
        
        {/* Select Booking Account connection type */}
        {channelName === 'booking' && connectedAccounts.length > 0 && !showBookingAccountDetails && (
          <BookingAccountsStep
            connectedAccounts={connectedAccounts}
            onSelectAccount={handleBookingAccountSelect}
            onAddNewAccount={handleAddAccount}
            onBack={handleBackToChannels}
          />
        )}

        {channelName === 'booking' && showBookingAccountDetails && selectedAccount && (
          <>
            <BookingAccountsStep
              connectedAccounts={connectedAccounts}
              onSelectAccount={handleBookingAccountSelect}
              onAddNewAccount={handleAddAccount}
              onBack={handleBackToBookingAccounts}
              headerOnly={true}
            />
            
            <BookingAccountSection
              account={selectedAccount}
              selectedSubAccountIndex={selectedSubAccountIndex}
              onSelectSubAccount={handleSubAccountChange}
              onImportNewUnits={handleImportNewUnits}
              onRefreshSync={handleOpenRefreshSyncModal}
              onBack={handleBackToBookingAccounts}
              onAddProperty={handleAddProperty}
              onOpenSettings={handleOpenSettings}
              onReconnect={handleResyncAccount}
              isRefreshing={isFetchingUnits}
              isReconnecting={resyncAccountMutation.isPending}
            />
            
            <ImportedUnitsTable
              units={filteredUnits}
              isLoading={isLoadingUnits}
              isError={isUnitsError}
              error={unitsError}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onRetry={() => refetchUnits()}
              onReconnect={handleAddAccount}
              hasIncompleteConnection={!channelId && !isLoadingChannel}
              channelDisplayName={isRtl ? channelInfo.displayNameAr : channelInfo.displayName}
              onAction={handleUnitAction}
            />
          </>
        )}

        {channelName !== 'booking' && (
          <>
            <ManagePageCard>
              {selectedAccount && (
                <AccountInfoBar
                  account={selectedAccount}
                  channelName={channelName}
                  onImportNewUnits={handleImportNewUnits}
                  onRefreshSync={handleOpenRefreshSyncModal}
                  onOpenSettings={handleOpenSettings}
                  onReconnect={handleResyncAccount}
                  isRefreshing={isFetchingUnits}
                  isReconnecting={resyncAccountMutation.isPending}
                />
              )}
            </ManagePageCard>

            {/* Disconnected Account Alert - Under the account info card */}
            {selectedAccount && !selectedAccount.isChannelActive && (
              <DisconnectedAlertContainer>
                <DisconnectedAlertIcon>
                  <AlertIcon />
                </DisconnectedAlertIcon>
                <DisconnectedAlertText>
                  {t(T_KEYS.DISCONNECTED_ACCOUNT_ALERT, { channel: channelName })}
                </DisconnectedAlertText>
              </DisconnectedAlertContainer>
            )}

            <ManagePageCard>
              <ImportedUnitsTable
              units={filteredUnits}
              isLoading={isLoadingUnits}
              isError={isUnitsError}
              error={unitsError}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onRetry={() => refetchUnits()}
              onReconnect={handleAddAccount}
              hasIncompleteConnection={!channelId && !isLoadingChannel}
              channelDisplayName={isRtl ? channelInfo.displayNameAr : channelInfo.displayName}
              onAction={handleUnitAction}
            />
            </ManagePageCard>
          </>
        )}
      </ManagePageContent>

      {/* Refresh Sync Confirmation Modal */}
      <RefreshSyncConfirmModal
        isOpen={isRefreshSyncModalOpen}
        onClose={handleCloseRefreshSyncModal}
        onConfirm={handleConfirmRefreshSync}
        isLoading={isRefreshSyncLoading}
      />

      {/* Add Booking Property Modal */}
      {channelName === 'booking' && selectedAccount && (
        <AddBookingPropertyModal
          open={showAddPropertyModal}
          onClose={handleAddPropertyModalClose}
          connectionType={selectedAccount.connectionType}
          mainChannelId={selectedAccount.id}
          onSuccess={handleAddPropertySuccess}
        />
      )}

      {/* All Units Synced Modal */}
      {showAllUnitsSyncedModal && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.48)',
              backdropFilter: 'blur(2.6px)',
              zIndex: 1000,
            }}
            onClick={() => setShowAllUnitsSyncedModal(false)}
          />
          {/* Modal */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '20px',
              padding: '32px',
              width: '480px',
              maxWidth: '90vw',
              boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.1)',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              alignItems: 'center',
            }}
          >
            {/* Warning Icon */}
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '28px',
                background: '#fff4d6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="#F59E0B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Modal Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'center', width: '100%' }}>
              <h3
                style={{
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '20px',
                  fontWeight: 700,
                  lineHeight: '28px',
                  color: '#262626',
                }}
              >
                {t(T_KEYS.NO_NEW_UNITS_TITLE)}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  color: '#666',
                }}
              >
                {t(T_KEYS.NO_NEW_UNITS_DESC, { channel: isRtl ? channelInfo.displayNameAr : channelInfo.displayName })}
              </p>
            </div>

            {/* OK Button */}
            <button
              onClick={() => setShowAllUnitsSyncedModal(false)}
              style={{
                width: '100%',
                height: '44px',
                border: 'none',
                borderRadius: '8px',
                background: '#5b2ff9',
                fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#4a25d4';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#5b2ff9';
              }}
            >
              {t(T_KEYS.OK)}
            </button>
          </div>
        </>
      )}

      {/* Settings Modal */}
      {showSettingsModal && selectedAccount && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.48)',
              backdropFilter: 'blur(2.6px)',
              zIndex: 1000,
            }}
            onClick={handleCloseSettings}
          />
          {/* Modal */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '24px',
              width: '520px',
              maxWidth: '90vw',
              boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.1)',
              zIndex: 1001,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 24px',
                height: '81px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '20px',
                  fontWeight: 700,
                  lineHeight: '28px',
                  color: '#262626',
                }}
              >
                {t(T_KEYS.ACCOUNT_SETTINGS)}
              </h2>
              <button
                onClick={handleCloseSettings}
                style={{
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Account Name */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '22.5px' }}>
                <span style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif", fontSize: '14px', fontWeight: 400, color: '#6b7280' }}>
                  {t(T_KEYS.ACCOUNT_NAME)}
                </span>
                <span style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif", fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                  {t(T_KEYS.ACCOUNT_DISPLAY_NAME, { channel: isRtl ? channelInfo.displayNameAr : channelInfo.displayName, id: selectedAccount.id })}
                </span>
              </div>

              {/* Host ID */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '22.5px' }}>
                <span style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif", fontSize: '14px', fontWeight: 400, color: '#6b7280' }}>
                  {t(T_KEYS.ACCOUNT_ID_HOST_ID)}
                </span>
                <span style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif", fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                  {selectedAccount.channelId || selectedAccount.id}
                </span>
              </div>

              {/* Connection Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '31.5px' }}>
                <span style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif", fontSize: '14px', fontWeight: 400, color: '#6b7280' }}>
                  {t(T_KEYS.CONNECTION_STATUS)}
                </span>
                <div
                  style={{
                    padding: '4px 16px',
                    borderRadius: '4px',
                    background: selectedAccount.isChannelActive ? '#dcfce7' : '#fee2e2',
                    border: `1px solid ${selectedAccount.isChannelActive ? '#bbf7d0' : '#fecaca'}`,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '16px',
                      color: selectedAccount.isChannelActive ? '#15803d' : '#dc2626',
                    }}
                  >
                    {selectedAccount.isChannelActive 
                      ? t(T_KEYS.CONNECTED)
                      : t(T_KEYS.DISCONNECTED)
                    }
                  </span>
                </div>
              </div>

              {/* Last Sync */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '21px' }}>
                <span style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif", fontSize: '14px', fontWeight: 400, color: '#6b7280' }}>
                  {t(T_KEYS.LAST_SYNC)}
                </span>
                <span style={{ fontFamily: "'IBM Plex Sans Arabic', sans-serif", fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                  {selectedAccount.updateAt 
                    ? (() => {
                        const updateDate = new Date(selectedAccount.updateAt);
                        const now = new Date();
                        const diffMs = now.getTime() - updateDate.getTime();
                        const diffMins = Math.floor(diffMs / 60000);
                        if (diffMins < 60) {
                          return t(T_KEYS.LAST_SYNC_MINUTES, { minutes: diffMins });
                        }
                        const diffHours = Math.floor(diffMins / 60);
                        if (diffHours < 24) {
                          return t(T_KEYS.LAST_SYNC_HOURS, { hours: diffHours });
                        }
                        const diffDays = Math.floor(diffHours / 24);
                        return t(T_KEYS.LAST_SYNC_DAYS, { days: diffDays });
                      })()
                    : t(T_KEYS.NOT_AVAILABLE)
                  }
                </span>
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                padding: '21px 24px 24px',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              {/* Disconnect Button */}
              <button
                onClick={handleDisconnectClick}
                style={{
                  flex: 1,
                  height: '44px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#e54848',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '16px',
                  fontWeight: 400,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#d43d3d';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#e54848';
                }}
              >
                {t(T_KEYS.DISCONNECT)}
              </button>
              {/* Cancel Button */}
              <button
                onClick={handleCloseSettings}
                style={{
                  flex: 1,
                  height: '44px',
                  border: '1px solid #e4e4e7',
                  borderRadius: '8px',
                  background: 'white',
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#1a1a1a',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                {t(T_KEYS.CANCEL)}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectConfirmModal && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.48)',
              backdropFilter: 'blur(2.6px)',
              zIndex: 1002,
            }}
            onClick={handleDisconnectCancel}
          />
          {/* Modal */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '480px',
              maxWidth: '90vw',
              boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.1)',
              zIndex: 1003,
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {/* Modal Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: isRtl ? 'right' : 'left' }}>
              <h3
                style={{
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '20px',
                  fontWeight: 700,
                  lineHeight: '28px',
                  color: '#262626',
                }}
              >
                {t(T_KEYS.DISCONNECT_CONFIRM_TITLE)}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  color: '#666',
                }}
              >
                {t(T_KEYS.DISCONNECT_CONFIRM_DESC1)}
              </p>
              <p
                style={{
                  margin: 0,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  color: '#666',
                }}
              >
                {t(T_KEYS.DISCONNECT_CONFIRM_DESC2)}
              </p>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                {/* Cancel Button */}
                <button
                  onClick={handleDisconnectCancel}
                  disabled={disconnectAccountMutation.isPending}
                  style={{
                    flex: 1,
                    height: '44px',
                    border: '1px solid #e4e4e7',
                    borderRadius: '8px',
                    background: 'white',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#1a1a1a',
                    cursor: disconnectAccountMutation.isPending ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: disconnectAccountMutation.isPending ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!disconnectAccountMutation.isPending) {
                      e.currentTarget.style.background = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                  }}
                >
                  {t(T_KEYS.UNDO)}
                </button>
                {/* Disconnect Button */}
                <button
                  onClick={handleDisconnectConfirm}
                  disabled={disconnectAccountMutation.isPending}
                  style={{
                    flex: 1,
                    height: '44px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#e54848',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                    fontSize: '16px',
                    fontWeight: 400,
                    color: 'white',
                    cursor: disconnectAccountMutation.isPending ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: disconnectAccountMutation.isPending ? 0.8 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                  onMouseEnter={(e) => {
                    if (!disconnectAccountMutation.isPending) {
                      e.currentTarget.style.background = '#d43d3d';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#e54848';
                  }}
                >
                  {disconnectAccountMutation.isPending && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{
                        animation: 'spin 1s linear infinite',
                      }}
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray="31.4 31.4"
                        strokeDashoffset="0"
                      />
                    </svg>
                  )}
                  {t(T_KEYS.DISCONNECT)}
                </button>
              </div>
              {/* Support Link */}
              <a
                href="#"
                style={{
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  color: '#5a3fff',
                  textDecoration: 'none',
                }}
              >
                {t(T_KEYS.CONTACT_SUPPORT)}
              </a>
            </div>
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default ManageChannelView;
